import { Button, Grid } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { InvestmentFilter } from "../../components/common/InvestmentFilter";
import FilterTable from "../../components/table/FilterTable";
import DefaultPageWrapper from "../../components/wrapper/DefaultPageWrapper";
import LoaderWrapper from "../../components/wrapper/LoaderWrapper";
import { USER_ROLES } from "../../helpers/constants";
import { useAccessControl } from "../../hooks/auth/useCheckPermission";
import { useDialogBetter } from "../../hooks/useDialogBetter";
import usePaginationWithSearchParams from "../../hooks/usePaginationWithSearchParams";
import { useProducts } from "../../services/productsService";
import TableService from "../../services/tableService";
import { ProductConfigurationDialog } from "./ProductConfigurationDialog/ProductConfigurationDialog";
import { ProductEditImportListDialog } from "./ProductEditImportListDialog";
import {
    SALES_PRODUCTS_TABLE_NAME,
    useSalesProductsTableFieldsConfig,
} from "./SalesProductsTableFieldsConfig";
import { TextFilter } from "../../components/common/TextFilter";

export function SalesProductsPage(props) {
    const { t } = useTranslation();
    const SALES_PRODUCTS_TABLE_FIELDS_CONFIG = useSalesProductsTableFieldsConfig();

    const [dataLocal, setDataLocal] = useState([]);
    const [hiddenColumnsForTables, setHiddenColumnsForTables] = useState([]);
    const [columnsOrdersForTables, setColumnsOrdersForTables] = useState([]);
    const [recordsCount, setRecordsCount] = useState(0);
    const [selectedRowId, setSelectedRowId] = useState(null);
    const [checkedAll, setCheckedAll] = useState(false);
    const [importDialogOpen, setImportDialogOpen] = useState(false);

    const {
        resetPageNumber,
        searchParams,
        setSearchParams,
        page: dataPage,
        pageSize: dataPageSize,
        handleChangePageWithSearchParams: handleChangeDataPage,
        handleChangePageSizeWithSearchParams: handleChangeDataPageSize,
    } = usePaginationWithSearchParams();

    const { data: productRows, isLoading } = useProducts(searchParams);

    useEffect(() => {
        if (productRows?.results) {
            setDataLocal(productRows.results);
            setRecordsCount(productRows.count);
        }
    }, [productRows, dataPage, dataPageSize]);

    const onClickButton = useCallback((e, id) => {
        e.stopPropagation();
    }, []);

    const productsDataExportHeaders = useMemo(() => {
        return TableService.getTableExportHeaders(
            SALES_PRODUCTS_TABLE_FIELDS_CONFIG, // @ts-ignore
            hiddenColumnsForTables?.[SALES_PRODUCTS_TABLE_NAME] || [], // @ts-ignore
            columnsOrdersForTables?.[SALES_PRODUCTS_TABLE_NAME] || [],
        );
    }, [SALES_PRODUCTS_TABLE_FIELDS_CONFIG, hiddenColumnsForTables, columnsOrdersForTables]);

    const data = useMemo(
        () =>
            TableService.getPreparedDataForCollapsibleTable(
                SALES_PRODUCTS_TABLE_FIELDS_CONFIG,
                dataLocal, // @ts-ignore
                hiddenColumnsForTables?.[SALES_PRODUCTS_TABLE_NAME]
                    ? // @ts-ignore
                      hiddenColumnsForTables?.[SALES_PRODUCTS_TABLE_NAME]
                    : [], // @ts-ignore
                columnsOrdersForTables?.[SALES_PRODUCTS_TABLE_NAME]
                    ? // @ts-ignore
                      columnsOrdersForTables?.[SALES_PRODUCTS_TABLE_NAME]
                    : [],
                onClickButton,
            ),
        [
            SALES_PRODUCTS_TABLE_FIELDS_CONFIG,
            dataLocal,
            hiddenColumnsForTables,
            columnsOrdersForTables,
            onClickButton,
        ],
    );
    const { openDialog } = useDialogBetter();
    const openDialogWithProducts = () => {
        // @ts-ignore
        const selectedProducts = dataLocal.filter((row) => row.selected);

        openDialog({
            Component: ProductConfigurationDialog,
            componentProps: { products: selectedProducts },
        });
    };

    const hasSelectedRows = useMemo(() => {
        // @ts-ignore
        return dataLocal.some((row) => row.selected);
    }, [dataLocal]);

    const { hasAccess } = useAccessControl([USER_ROLES.ADMIN, USER_ROLES.SALES_MANAGER]);

    if (!hasAccess) {
        return null;
    }

    const toolbarComponent = () => (
        <Grid container spacing={10} justifyContent="right">
            <Grid item>
                <Button variant="contained" onClick={() => setImportDialogOpen(true)}>
                    {t("products.importList")}
                </Button>
            </Grid>
            <Grid item>
                <Button
                    variant="contained"
                    color="secondary"
                    disabled={!hasSelectedRows}
                    onClick={openDialogWithProducts}
                >
                    {t("products.massAction")}
                </Button>
            </Grid>
        </Grid>
    );

    const tableComponent = () => (
        <FilterTable
            // @ts-ignore
            data={data}
            countRecords={recordsCount}
            resetPageNumber={resetPageNumber}
            selectedItemId={selectedRowId}
            style={{ maxHeight: "60vh" }}
            headersConfig={TableService.orderColumns(
                TableService.hideHiddenColumns(
                    SALES_PRODUCTS_TABLE_FIELDS_CONFIG, // @ts-ignore
                    hiddenColumnsForTables?.[SALES_PRODUCTS_TABLE_NAME] || [],
                ),
                // @ts-ignore
                columnsOrdersForTables?.[SALES_PRODUCTS_TABLE_NAME] || [],
            )}
            showCleanFilterIcon={true}
            showContextMenu={false}
            showExportToFileButton={true}
            exportToFileUrl={"products/export/"}
            exportToFileSearchParams={searchParams}
            exportToFileHeaders={productsDataExportHeaders}
            exportToFileFileName={`${t("products.exportFileName")}.xlsx`}
            showDetailsIcon={false}
            showCheckbox={true}
            checkedAll={checkedAll}
            page={dataPage}
            onPageChange={handleChangeDataPage}
            rowsPerPage={dataPageSize}
            onRowsPerPageChange={handleChangeDataPageSize}
            setDataLocal={setDataLocal}
            setCheckedAll={setCheckedAll}
            setSelectedRowId={setSelectedRowId}
            isLoading={isLoading}
        />
    );

    return (
        // @ts-ignore
        <DefaultPageWrapper tagTitle={t("headers.products")}>
            <Grid
                item
                container
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
                rowSpacing={1}
            >
                <Grid item xs={4}>
                    <TextFilter
                        filterKey={"master"}
                        searchParams={searchParams}
                        setSearchParams={setSearchParams}
                        resetPageNumber={resetPageNumber}
                        withPagination
                        placeholder={t("products.masterFilterPlaceholder") ?? undefined}
                    />
                </Grid>
                <Grid item container direction="row" alignItems="center" spacing={3}>
                    <Grid item xs={2}>
                        <InvestmentFilter
                            searchParams={searchParams}
                            setSearchParams={setSearchParams}
                        />
                    </Grid>
                    <Grid item xs={10}>
                        {toolbarComponent()}
                    </Grid>
                </Grid>

                <Grid item xs={12}>
                    <LoaderWrapper showLoader={false}>{tableComponent()}</LoaderWrapper>
                </Grid>
            </Grid>
            <ProductEditImportListDialog
                title={t("products.importList")}
                open={importDialogOpen}
                onClose={() => setImportDialogOpen(false)}
            />
        </DefaultPageWrapper>
    );
}
